import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { update } from 'store/slices/user';
import { toastSuccess } from 'components/Toast';
import useCurrentUser from 'hooks/useCurrentUser';
import { useUserJobs } from 'components/dropdowns/UserJobs';
import { updateUserMe, updateUserMeJobs } from 'actions/userMe';
import { useClassFamiliarity } from 'components/dropdowns/ClassFamiliarity';
import {
  Button,
  Select,
  MultiSelect,
  Text
} from '@teachstonellc/component-library';
import { useForm, Controller } from 'react-hook-form';

interface ExperienceFormValues {
  jobs: string[];
  class_experience: number | undefined;
}

function Experience() {
  const { run, isLoading } = useAsync();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const userJobsOptions = useUserJobs();
  const classFamiliarityOptions = useClassFamiliarity();

  const { control, handleSubmit, reset, formState } =
    useForm<ExperienceFormValues>({
      defaultValues: {
        jobs: currentUser.jobs,
        class_experience: currentUser.class_experience ?? undefined
      }
    });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (formState.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formState.isDirty]);

  function onSubmit(values: ExperienceFormValues) {
    run(
      Promise.all([
        updateUserMe({
          class_experience: values.class_experience
        }),
        updateUserMeJobs({ jobs: values.jobs })
      ]),
      {
        onSuccess: () => {
          dispatch(update({ ...currentUser, ...values }));
          reset(values);
          toastSuccess();
        }
      }
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-4 rounded-md bg-white shadow-sm border border-neutral-200">
        <Text
          as="h2"
          className="font-bold uppercase text-[#58697b] mb-4 pb-4 text-ds-sm"
        >
          {i18n.ft(messages.title)}
        </Text>

        <div className="mb-4">
          <Controller
            name="jobs"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label={i18n.ft(messages.roles)}
                options={userJobsOptions}
                values={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="mb-4">
          <Controller
            name="class_experience"
            control={control}
            render={({ field }) => (
              <Select
                label={i18n.ft(messages.familiarity)}
                options={classFamiliarityOptions}
                value={field.value ?? null}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          variant="primary"
          shape="rounded"
          loading={isLoading}
          disabled={isLoading || !formState.isDirty}
        >
          {i18n.ft(messages.save)}
        </Button>
      </div>
    </form>
  );
}

export default Experience;
